import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import ParkCard from "@/components/ParkCard";
import FilterSidebar from "@/components/FilterSidebar";
import CompareDrawer from "@/components/CompareDrawer";
import { ParkCardSkeletonGrid } from "@/components/Skeletons";
import { api } from "@/lib/api";
import { geocodePostcode, haversineKm, estimateDriveMinutes, looksLikePostcode } from "@/lib/geo";
import { SlidersHorizontal, AlertCircle, Home as HomeIcon } from "lucide-react";

const parseArr = (v) => (v ? v.split(",").filter(Boolean) : []);

export default function Search() {
  const [params] = useSearchParams();
  const initial = useMemo(() => Object.fromEntries(params.entries()), [params]);

  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState("recommended");
  const [results, setResults] = useState(null);
  const [meta, setMeta] = useState({ regions: [], operators: [], facilities: [] });
  const [showFilters, setShowFilters] = useState(false);

  // Re-sync filters + sort whenever URL changes (in-page SearchBar resubmits)
  useEffect(() => {
    setFilters({
      operator: parseArr(initial.operator),
      facility: parseArr(initial.facility),
      region: initial.region,
      rating_min: initial.rating_min ? Number(initial.rating_min) : undefined,
      price_min: initial.price_min ? Number(initial.price_min) : undefined,
      price_max: initial.price_max ? Number(initial.price_max) : undefined,
      pet_friendly: initial.pet_friendly === "true",
      adults_only: initial.adults_only === "true",
    });
    setSort(initial.sort || (initial.postcode ? "nearest" : "recommended"));
  }, [initial.operator, initial.facility, initial.region, initial.rating_min, initial.price_min, initial.price_max, initial.pet_friendly, initial.adults_only, initial.postcode, initial.sort]);

  // Postcode geocoding
  const [userLoc, setUserLoc] = useState(null);     // {lat, lng, formatted}
  const [postcodeError, setPostcodeError] = useState(null);

  useEffect(() => {
    Promise.all([api.regions(), api.operators(), api.facilities()]).then(([regions, operators, facilities]) =>
      setMeta({ regions, operators, facilities })
    );
  }, []);

  // Geocode postcode once when present in URL
  useEffect(() => {
    setUserLoc(null);
    setPostcodeError(null);
    const pc = initial.postcode;
    if (!pc) return;
    if (!looksLikePostcode(pc)) {
      setPostcodeError(`"${pc}" doesn't look like a UK postcode`);
      return;
    }
    geocodePostcode(pc).then((loc) => {
      if (!loc) setPostcodeError(`Couldn't find UK postcode "${pc}"`);
      else setUserLoc(loc);
    });
  }, [initial.postcode]);

  useEffect(() => {
    setResults(null);
    let cancelled = false;
    const q = {
      arrival: initial.arrival,
      departure: initial.departure,
      adults: initial.adults,
      children: initial.children,
      pets: initial.pets === "true" ? true : undefined,
      sort: sort === "nearest" ? "recommended" : sort,
      ...filters,
    };
    api.searchParks(q).then((data) => { if (!cancelled) setResults(data); });
    return () => { cancelled = true; };
  }, [initial.arrival, initial.departure, initial.adults, initial.children, initial.pets, sort, filters]);

  // Enrich results with distance + drive time when user location is known
  const enriched = useMemo(() => {
    if (!results) return null;
    if (!userLoc) return results.results;
    const out = results.results.map((p) => {
      const km = haversineKm(userLoc.lat, userLoc.lng, p.lat, p.lng);
      return { ...p, distance_km: km, drive_minutes: estimateDriveMinutes(km) };
    });
    if (sort === "nearest") out.sort((a, b) => a.distance_km - b.distance_km);
    return out;
  }, [results, userLoc, sort]);

  return (
    <div className="App">
      <Nav />
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <SearchBar compact initial={initial} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className={`lg:col-span-3 ${showFilters ? "block" : "hidden lg:block"}`}>
          <FilterSidebar filters={filters} setFilters={setFilters} regions={meta.regions} operators={meta.operators} facilities={meta.facilities} />
        </div>

        <div className="lg:col-span-9">
          <div className="bg-sky/10 border border-sky/30 rounded-xl px-4 py-3 mb-4 text-sm text-ink/80 leading-relaxed" data-testid="results-disclaimer">
            <strong className="text-ink">Prices shown are indicative.</strong> Live availability and final pricing are confirmed on the operator's own site — always click <em>Check availability</em> to verify before booking.
          </div>

          {postcodeError && (
            <div className="bg-terracotta/10 border border-terracotta/30 rounded-xl px-4 py-3 mb-4 text-sm text-ink/80 inline-flex items-center gap-2" data-testid="postcode-error">
              <AlertCircle className="w-4 h-4 text-terracotta shrink-0" />{postcodeError}
            </div>
          )}
          {userLoc && (
            <div className="bg-forest/10 border border-forest/30 rounded-xl px-4 py-3 mb-4 text-sm text-ink/80 inline-flex items-center gap-2" data-testid="postcode-ok">
              <HomeIcon className="w-4 h-4 text-forest shrink-0" />Distance &amp; drive time shown from <strong className="text-ink">{userLoc.formatted}</strong>
            </div>
          )}

          <div className="flex items-center justify-between mb-5">
            <div data-testid="results-summary">
              <h1 className="font-display font-bold text-2xl">
                {enriched ? `${enriched.length} parks` : "Searching..."}
                {filters.region ? <span className="text-ink/60"> in {filters.region}</span> : null}
              </h1>
              <p className="text-sm text-ink/60 mt-0.5">
                {initial.arrival && initial.departure ? `${initial.arrival} → ${initial.departure}` : "Pick your dates above"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="btn-ghost text-sm lg:hidden"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-mobile"
              >
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </button>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-white border border-border rounded-full px-4 py-2.5 text-sm outline-none"
                data-testid="sort-select"
              >
                <option value="recommended">Recommended</option>
                <option value="cheapest">Cheapest first</option>
                <option value="rated">Best rated</option>
                <option value="beach">Nearest beach</option>
                {userLoc && <option value="nearest">Closest to me</option>}
              </select>
            </div>
          </div>

          {!enriched ? (
            <ParkCardSkeletonGrid count={6} />
          ) : enriched.length === 0 ? (
            <div className="text-center py-20 bg-white border border-border rounded-2xl" data-testid="no-results">
              <h3 className="font-display text-xl font-semibold">No parks match your filters</h3>
              <p className="text-ink/60 text-sm mt-2">Try widening your price range or removing some facilities.</p>
              <button onClick={() => setFilters({})} className="btn-primary mt-5">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="results-grid">
              {enriched.map((p) => <ParkCard key={p.slug} park={p} />)}
            </div>
          )}
        </div>
      </div>
      <CompareDrawer />
      <Footer />
    </div>
  );
}
